// Название открытой поездки прямо в полосе инструментов.
//
// В списке поездок название и раньше правилось, но планировщик о нём молчал:
// человек с четырьмя поездками работал в безымянном экране и узнавал, где он,
// только вернувшись в список. А ещё название — это заголовок вкладки и
// заголовок карточки, которую увидит тот, кому дали ссылку: место, где его
// правят, должно быть там же, где на него смотрят.
//
// Распоряжается названием владелец: соавтор видит его, но не меняет — то же
// правило, что и в списке поездок, и то же, что на сервере (403 на PATCH).
import { useEffect, useState, type ReactElement } from 'react';
import { useT } from '../i18n';
import { useTrips } from './TripsProvider';
import styles from './TripNameField.module.css';

export function TripNameField(): ReactElement | null {
  const t = useT();
  const { current, rename } = useTrips();
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState('');

  // Переключились на другую поездку — правка отменяется: черновик относился
  // к прежней, и сохранить его в новую было бы подменой.
  useEffect(() => {
    setEditing(false);
  }, [current?.id]);

  // Поездки нет вовсе: у гостя или пока список не пришёл. Показывать пустое
  // место под название незачем — оно бы дёргало полосу при загрузке.
  if (!current) return null;

  const title = current.name.trim() || t('trips.untitled');

  if (current.role !== 'owner') {
    return (
      <span className={styles.readonly} title={t('trips.rename.ownerOnly')}>
        {title}
      </span>
    );
  }

  function commit(): void {
    setEditing(false);
    const next = draft.trim();
    if (current && next !== current.name) void rename(current.id, next);
  }

  if (!editing) {
    return (
      <button
        type="button"
        className={styles.name}
        title={t('trips.rename')}
        onClick={() => {
          setDraft(current.name);
          setEditing(true);
        }}
      >
        {title}
      </button>
    );
  }

  return (
    <input
      className={`input ${styles.input}`}
      value={draft}
      maxLength={80}
      autoFocus
      aria-label={t('trips.name')}
      onChange={(e) => setDraft(e.target.value)}
      onBlur={commit}
      onKeyDown={(e) => {
        if (e.key === 'Enter') commit();
        if (e.key === 'Escape') {
          setDraft(current.name);
          setEditing(false);
        }
      }}
    />
  );
}
